import {
  ArrowLeft,
  Building2,
  CloudUpload,
  KeyRound,
  User,
  Users,
} from 'lucide-react';
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { canAccessApiKeyManagement } from '../../utils/apiKeyAccess';
import { EASE_OUT, SectionLabel } from '../portal';
import { NavRow } from './NavRow';

interface SettingsViewProps {
  onBackToApp: () => void;
  onMyProfileClick: () => void;
  shouldAnimate?: boolean;
  onLinkClick?: () => void;
  canManageTeam?: boolean;
  hasPublisherProfile?: boolean;
}

interface SettingsItem {
  icon: ReactNode;
  label: string;
  id: string;
  route: string;
  requiresManageTeam?: boolean;
}

interface SettingsGroup {
  label: string;
  items: SettingsItem[];
}

/** Main ⇄ settings panel slide shared by `Sidebar`; 180ms ease-out, no spring. */
export const PANEL_TRANSITION = { duration: 0.18, ease: EASE_OUT } as const;

const settingsGroups: SettingsGroup[] = [
  {
    label: 'Account',
    items: [
      {
        icon: <User />,
        label: 'Profile',
        id: 'my-profile',
        route: '/dashboard/settings/profile',
      },
      {
        icon: <CloudUpload />,
        label: 'Storage',
        id: 'preferences',
        route: '/dashboard/settings/preferences',
      },
    ],
  },
  {
    label: 'Publisher',
    items: [
      {
        icon: <Building2 />,
        label: 'Publisher',
        id: 'publisher-profile',
        route: '/dashboard/settings/publisher',
      },
      {
        icon: <Users />,
        label: 'Team',
        id: 'team',
        route: '/dashboard/settings/team',
        requiresManageTeam: true,
      },
      {
        icon: <KeyRound />,
        label: 'API Keys',
        id: 'developer-api-keys',
        route: '/dashboard/settings/api-keys',
        requiresManageTeam: true,
      },
    ],
  },
];

export const SettingsView = ({
  onBackToApp,
  onMyProfileClick,
  shouldAnimate = true,
  onLinkClick,
  canManageTeam,
  hasPublisherProfile = true,
}: SettingsViewProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const visibleGroups = settingsGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          (item.id !== 'publisher-profile' || hasPublisherProfile) &&
          (!item.requiresManageTeam ||
            canAccessApiKeyManagement(canManageTeam)),
      ),
    }))
    .filter((group) => group.items.length > 0);

  const handleItemClick = (item: SettingsItem) => {
    if (item.id === 'my-profile') {
      onMyProfileClick();
    } else {
      navigate(item.route);
      onLinkClick?.();
    }
  };

  return (
    <motion.div
      key="settings"
      initial={shouldAnimate ? { x: '100%' } : false}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={PANEL_TRANSITION}
      className="absolute inset-0 flex flex-col gap-1 pt-2"
    >
      {/* Side padding lives on the children, not the panel, so focus rings
          inside the scroll container are never clipped. */}
      <button
        type="button"
        onClick={onBackToApp}
        className="mx-2 flex h-[72px] shrink-0 items-center gap-3 rounded-lg bg-black px-4 text-white transition-colors duration-150 hover:bg-seeker-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-950"
      >
        <ArrowLeft className="size-4 shrink-0" />
        <span className="text-sm font-bold leading-5">Back to App</span>
      </button>

      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 pb-2">
        {visibleGroups.map((group) => (
          <div key={group.label} className="flex flex-col gap-1">
            <SectionLabel className="px-2 pb-2 pt-4">
              {group.label}
            </SectionLabel>
            <nav
              aria-label={`${group.label} settings`}
              className="flex flex-col gap-1"
            >
              {group.items.map((item) => (
                <NavRow
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  aria-label={item.label}
                  active={currentPath === item.route}
                  onClick={() => handleItemClick(item)}
                />
              ))}
            </nav>
          </div>
        ))}
      </div>
    </motion.div>
  );
};
